import { useEffect, useState } from 'react';
import { Button as UiButton, FormField, Input, Switch, Textarea } from '@coordio/ui';
import { FormSection, InlineErrorPanel, LoadingSkeleton } from '@coordio/ui/layout';
import { settingsService } from '../../services/settingsService';
import { useAuthStore } from '../../store/authStore';
import { useNotificationStore } from '../../store/notificationStore';
import type { UpdateProjectSettingsRequest } from '../../types/settings';
import { canManageProjectSettings } from '../../utils/globalAccess';

interface ProjectSettingsFormProps {
  projectId: string;
}

export const ProjectSettingsForm = ({ projectId }: ProjectSettingsFormProps) => {
  const { addNotification } = useNotificationStore();
  const token = useAuthStore((s) => s.token);
  const userRoles = useAuthStore((s) => s.user?.roles ?? []);
  const canManage = canManageProjectSettings(token, userRoles);

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [form, setForm] = useState<UpdateProjectSettingsRequest>({
    maxAgentsPerProject: null,
    maxSessionsPerDay: null,
    maxStorageBytes: null,
    dailyBudgetLimit: 100,
    monthlyBudgetLimit: 1000,
    budgetAlertsEnabled: true,
    budgetAlertThreshold: 0.8,
    ipFencingEnabled: false,
    ipAllowlistJson: null,
    notificationSettingsJson: null,
    resourceLimitsJson: null,
    metadataJson: null,
  });

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        const settings = await settingsService.getProjectSettings(projectId);
        setForm({
          maxAgentsPerProject: settings.maxAgentsPerProject ?? null,
          maxSessionsPerDay: settings.maxSessionsPerDay ?? null,
          maxStorageBytes: settings.maxStorageBytes ?? null,
          dailyBudgetLimit: settings.dailyBudgetLimit ?? 100,
          monthlyBudgetLimit: settings.monthlyBudgetLimit ?? 1000,
          budgetAlertsEnabled: settings.budgetAlertsEnabled,
          budgetAlertThreshold: settings.budgetAlertThreshold ?? 0.8,
          ipFencingEnabled: settings.ipFencingEnabled,
          ipAllowlistJson: settings.ipAllowlistJson ?? null,
          notificationSettingsJson: settings.notificationSettingsJson ?? null,
          resourceLimitsJson: settings.resourceLimitsJson ?? null,
          metadataJson: settings.metadataJson ?? null,
        });
      } catch (err: any) {
        setError(err.response?.data?.message || err.message || 'Failed to load project settings');
      } finally {
        setLoading(false);
      }
    };

    load();
  }, [projectId]);

  const save = async () => {
    if (!canManage) {
      return;
    }

    setSaving(true);
    setError(null);
    try {
      await settingsService.updateProjectSettings(projectId, form);
      addNotification('Project settings saved', 'success');
    } catch (err: any) {
      const msg = err.response?.data?.message || err.message || 'Failed to save project settings';
      setError(msg);
      addNotification(msg, 'error');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <LoadingSkeleton rows={6} data-testid="project-settings-detail-loading" />;
  }

  return (
    <div className="flex flex-col gap-8" data-testid="project-settings-detail-page">
      {error && (
        <InlineErrorPanel
          message={error}
          action={
            <UiButton type="button" variant="ghost" size="sm" onClick={() => setError(null)}>
              Dismiss
            </UiButton>
          }
          data-testid="project-settings-detail-error"
        />
      )}

      {!canManage && (
        <InlineErrorPanel
          title="Read-only"
          message="Project settings, including IP fencing, can be changed only by a project administrator."
        />
      )}

      <FormSection title="Limits" description="Control the maximum resources this project can consume." data-testid="project-settings-section-limits">
        <div className="grid gap-5 items-end sm:grid-cols-2">
          <FormField label="Max agents per project" description="Maximum number of agents allowed in this project." data-testid="project-settings-maxAgents">
            <Input
              className="w-full"
              type="number"
              value={form.maxAgentsPerProject ?? ''}
              onChange={(e) =>
                setForm((s) => ({ ...s, maxAgentsPerProject: e.target.value === '' ? null : Number(e.target.value) }))
              }
              min={0}
              disabled={!canManage}
              data-testid="project-settings-maxAgents-input"
            />
          </FormField>
          <FormField label="Max sessions per day" description="Maximum number of sessions allowed per day." data-testid="project-settings-maxSessions">
            <Input
              className="w-full"
              type="number"
              value={form.maxSessionsPerDay ?? ''}
              onChange={(e) =>
                setForm((s) => ({ ...s, maxSessionsPerDay: e.target.value === '' ? null : Number(e.target.value) }))
              }
              min={0}
              disabled={!canManage}
              data-testid="project-settings-maxSessions-input"
            />
          </FormField>
        </div>
        <div className="mt-5">
          <div className="grid gap-5 items-end sm:grid-cols-2">
            <FormField label="Max storage (bytes)" description="Maximum storage this project can use." data-testid="project-settings-maxStorage">
              <Input
                className="w-full"
                type="number"
                value={form.maxStorageBytes ?? ''}
                onChange={(e) =>
                  setForm((s) => ({ ...s, maxStorageBytes: e.target.value === '' ? null : Number(e.target.value) }))
                }
                min={0}
                disabled={!canManage}
                data-testid="project-settings-maxStorage-input"
              />
            </FormField>
          </div>
        </div>
      </FormSection>

      <FormSection title="Budget" description="Set daily and monthly spending limits for this project." data-testid="project-settings-section-budget">
        <div className="grid gap-5 items-end sm:grid-cols-2">
          <FormField label="Daily budget limit ($)" description="Maximum allowed: $500" data-testid="project-settings-dailyBudget">
            <Input
              className="w-full"
              type="number"
              value={form.dailyBudgetLimit ?? ''}
              onChange={(e) =>
                setForm((s) => ({ ...s, dailyBudgetLimit: e.target.value === '' ? null : Number(e.target.value) }))
              }
              min={0}
              max={500}
              step={0.01}
              disabled={!canManage}
              data-testid="project-settings-dailyBudget-input"
            />
          </FormField>
          <FormField label="Monthly budget limit ($)" description="Maximum allowed: $5,000" data-testid="project-settings-monthlyBudget">
            <Input
              className="w-full"
              type="number"
              value={form.monthlyBudgetLimit ?? ''}
              onChange={(e) =>
                setForm((s) => ({ ...s, monthlyBudgetLimit: e.target.value === '' ? null : Number(e.target.value) }))
              }
              min={0}
              max={5000}
              step={0.01}
              disabled={!canManage}
              data-testid="project-settings-monthlyBudget-input"
            />
          </FormField>
        </div>

        <div className="mt-5">
          <div className="grid gap-5 items-end sm:grid-cols-2">
            <FormField label="Budget alert threshold (0–1)" description="Fraction of the budget at which an alert is triggered." data-testid="project-settings-budgetThreshold">
              <Input
                className="w-full"
                type="number"
                value={form.budgetAlertThreshold ?? ''}
                onChange={(e) =>
                  setForm((s) => ({ ...s, budgetAlertThreshold: e.target.value === '' ? null : Number(e.target.value) }))
                }
                disabled={!canManage || !form.budgetAlertsEnabled}
                min={0}
                max={1}
                step={0.01}
                data-testid="project-settings-budgetThreshold-input"
              />
            </FormField>
          </div>
        </div>

        <div className="mt-5">
          <div className="ui-form-choice-field">
            <div className="ui-form-choice-field__row">
              <div className="ui-form-choice-field__control">
                <Switch
                  checked={form.budgetAlertsEnabled}
                  onCheckedChange={(checked) => setForm((s) => ({ ...s, budgetAlertsEnabled: checked }))}
                  disabled={!canManage}
                  data-testid="project-settings-budgetAlerts-switch"
                />
              </div>
              <div className="ui-form-choice-field__label-cell">
                <span className="ui-form-choice-field__label">Enable budget alerts</span>
              </div>
            </div>
          </div>
        </div>
      </FormSection>

      <FormSection title="IP Fencing" description="Restrict access by IP address or CIDR range." data-testid="project-settings-section-ip-fencing">
        <div className="ui-form-choice-field">
          <div className="ui-form-choice-field__row">
            <div className="ui-form-choice-field__control">
              <Switch
                checked={form.ipFencingEnabled}
                onCheckedChange={(checked) => setForm((s) => ({ ...s, ipFencingEnabled: checked }))}
                disabled={!canManage}
                data-testid="project-settings-ipFencing-switch"
              />
            </div>
            <div className="ui-form-choice-field__label-cell">
              <span className="ui-form-choice-field__label">Enable IP fencing</span>
            </div>
          </div>
        </div>

        <div className="mt-5">
          <FormField label="IP Allowlist" description="Enter IP addresses or CIDR ranges, one per line (e.g., 10.0.0.1, 192.168.1.0/24)." data-testid="project-settings-ipAllowlist">
            <Textarea
              className="w-full"
              value={form.ipAllowlistJson ?? ''}
              onChange={(e) =>
                setForm((s) => ({ ...s, ipAllowlistJson: e.target.value || null }))
              }
              disabled={!canManage || !form.ipFencingEnabled}
              rows={4}
              data-testid="project-settings-ipAllowlist-textarea"
            />
          </FormField>
        </div>
      </FormSection>

      {canManage && (
        <div className="ui-form-actions">
          <UiButton type="button" variant="default" onClick={save} disabled={saving} data-testid="save-project-settings-btn">
            {saving ? 'Saving...' : 'Save Settings'}
          </UiButton>
        </div>
      )}
    </div>
  );
};
